import polygonClipping, { type MultiPolygon, type Pair, type Polygon, type Ring } from 'polygon-clipping';
import { localToWorld, type Vec2 } from '@vp/core';
import { CoreTypes, type ObjectRecord, type Style } from '@vp/shared-types';
import { formOutline, rectForm } from '../forms';
import { flattenBezier, shapeOutline } from '../pathgeom';

/**
 * Shape operations on overlapping objects (union, subtract, intersect, exclude, divide).
 * Objects become polygons in world coordinates, the result is a set of polygons with holes —
 * each becomes a closed path object. Curves are flattened (an ellipse becomes a many-sided polygon).
 */
export type BooleanOp = 'union' | 'subtract' | 'intersect' | 'exclude' | 'divide';

export const BOOLEAN_OPS: BooleanOp[] = ['union', 'subtract', 'intersect', 'exclude', 'divide'];

/** Objects that have a filled outline: rectangles, stickers, ellipses, triangles, diamonds, closed paths. */
export function booleanSupported(o: ObjectRecord): boolean {
  switch (o.type) {
    case CoreTypes.rect:
    case CoreTypes.sticker:
    case CoreTypes.ellipse:
    case CoreTypes.triangle:
    case CoreTypes.diamond:
      return o.w > 0 && o.h > 0;
    case CoreTypes.path:
      return o.props['closed'] === true;
    default:
      return false;
  }
}

/** Same winding as a plain rectangle, so a mirrored form stays an outline and not a hole. */
function sameWinding(flat: number[]): number[] {
  let s = 0;
  for (let i = 0; i < flat.length; i += 2) {
    const j = (i + 2) % flat.length;
    s += flat[i] * flat[j + 1] - flat[j] * flat[i + 1];
  }
  if (s >= 0) return flat;
  const rev: number[] = [];
  for (let i = flat.length - 2; i >= 0; i -= 2) rev.push(flat[i], flat[i + 1]);
  return rev;
}

function toWorld(o: ObjectRecord, flat: number[]): Ring {
  const ring: Ring = [];
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const p: Vec2 = localToWorld(o, { x: flat[i], y: flat[i + 1] });
    ring.push([p.x, p.y]);
  }
  return ring;
}

/** The object's outline (and holes) as a polygon in world coordinates. */
export function objectPolygon(o: ObjectRecord): Polygon | null {
  let outer: number[];
  let holes: number[][] = [];
  switch (o.type) {
    case CoreTypes.rect:
    case CoreTypes.sticker: {
      // a flowchart form (document, parallelogram, …) is not the box around it
      const form = o.type === CoreTypes.rect ? rectForm(o) : null;
      const shaped = form ? formOutline(form, o.w, o.h, o.style.cornerRadius ?? 0, !!o.props['flip']) : null;
      outer = shaped?.closed
        ? sameWinding(shaped.points)
        : shapeOutline('rect', 0, 0, o.w, o.h, o.style.cornerRadius ?? (o.type === CoreTypes.sticker ? 4 : 0));
      break;
    }
    case CoreTypes.ellipse:
      outer = shapeOutline('ellipse', 0, 0, o.w, o.h, 0, Math.min(180, Math.max(48, Math.round(Math.hypot(o.w, o.h) / 3))));
      break;
    case CoreTypes.triangle:
      outer = shapeOutline('triangle', 0, 0, o.w, o.h);
      break;
    case CoreTypes.diamond:
      outer = shapeOutline('diamond', 0, 0, o.w, o.h);
      break;
    case CoreTypes.path: {
      if (o.props['closed'] !== true) return null;
      const pts = o.props['points'];
      outer = Array.isArray(pts) && pts.length >= 6 ? (pts as number[]) : flattenBezier((o.props['nodes'] as number[] | undefined) ?? [], true, Math.max(o.w, o.h) / 1000);
      if (Array.isArray(o.props['holes'])) holes = o.props['holes'] as number[][];
      break;
    }
    default:
      return null;
  }
  if (outer.length < 6) return null;
  return [toWorld(o, outer), ...holes.filter((h) => h.length >= 6).map((h) => toWorld(o, h))];
}

const asMulti = (p: Polygon): MultiPolygon => [p];
const isEmpty = (m: MultiPolygon): boolean => !m.length;

/** Result of `op` on polygons given bottom to top; each entry is one polygon (outer ring first, then holes). */
export function booleanPolygons(op: BooleanOp, polys: Polygon[]): Polygon[] {
  if (polys.length < 2) return [];
  const [first, ...rest] = polys.map(asMulti);
  switch (op) {
    case 'union':
      return polygonClipping.union(first, ...rest);
    case 'subtract':
      // front shapes cut out of the one at the back
      return polygonClipping.difference(first, ...rest);
    case 'intersect':
      return polygonClipping.intersection(first, ...rest);
    case 'exclude': {
      // parity: where an odd number of shapes overlap
      let acc: MultiPolygon = first;
      for (const m of rest) acc = polygonClipping.xor(acc, m);
      return acc;
    }
    case 'divide': {
      // every region that lies in a distinct set of shapes becomes its own piece
      let regions: MultiPolygon[] = [first];
      for (const s of rest) {
        const next: MultiPolygon[] = [];
        let remainder: MultiPolygon = s;
        for (const r of regions) {
          const inside = polygonClipping.intersection(r, s);
          const outside = polygonClipping.difference(r, s);
          if (!isEmpty(inside)) next.push(inside);
          if (!isEmpty(outside)) next.push(outside);
          remainder = isEmpty(remainder) ? remainder : polygonClipping.difference(remainder, r);
        }
        if (!isEmpty(remainder)) next.push(remainder);
        regions = next;
      }
      return regions.flat();
    }
  }
}

/** Drop the closing duplicate, repeated points and points on a straight line. */
export function cleanRing(ring: Ring, eps = 1e-6): Pair[] {
  let pts = ring.slice();
  if (pts.length > 1 && Math.abs(pts[0][0] - pts[pts.length - 1][0]) < eps && Math.abs(pts[0][1] - pts[pts.length - 1][1]) < eps) pts.pop();
  let changed = true;
  while (changed && pts.length > 3) {
    changed = false;
    const out: Pair[] = [];
    for (let i = 0; i < pts.length; i++) {
      const a = out.length ? out[out.length - 1] : pts[(i + pts.length - 1) % pts.length];
      const b = pts[i];
      const c = pts[(i + 1) % pts.length];
      const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
      const same = Math.hypot(b[0] - a[0], b[1] - a[1]) < eps;
      if (same || Math.abs(cross) < eps * Math.max(1, Math.hypot(c[0] - a[0], c[1] - a[1]))) changed = true;
      else out.push(b);
    }
    pts = out;
  }
  return pts;
}

export interface BooleanPiece {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Outline nodes (corner nodes, local), the flattened outline and the holes, all local to the box. */
  nodes: number[];
  points: number[];
  holes: number[][];
}

/** Closed-path geometry (local to its own bounding box) for every polygon of a result. */
export function piecesOf(polys: Polygon[]): BooleanPiece[] {
  const out: BooleanPiece[] = [];
  for (const poly of polys) {
    const outer = cleanRing(poly[0]);
    if (outer.length < 3) continue;
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const [x, y] of outer) {
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
    const local = (r: Pair[]) => r.flatMap(([x, y]) => [x - x0, y - y0]);
    const points = local(outer);
    const nodes = outer.flatMap(([x, y]) => [x - x0, y - y0, x - x0, y - y0, x - x0, y - y0]);
    const holes = poly
      .slice(1)
      .map((h) => cleanRing(h))
      .filter((h) => h.length >= 3)
      .map(local);
    out.push({ x: x0, y: y0, w: Math.max(x1 - x0, 0.01), h: Math.max(y1 - y0, 0.01), nodes, points, holes });
  }
  return out;
}

/** The look of the result: the back object's fill, outline and opacity (text and corner radius do not carry over). */
export function resultStyle(back: ObjectRecord): Style {
  const { text: _text, cornerRadius: _r, ...rest } = back.style;
  return { ...rest, opacity: rest.opacity ?? 1 };
}
